// @mui material components
import { useState, useEffect } from "react";
import Fab from "@mui/material/Fab";
import Collapse from "@mui/material/Collapse";
import Backdrop from "@mui/material/Backdrop";

// Astro Clean Carpets components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";

// @mui icons
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import CloseIcon from "@mui/icons-material/Close";
import MessageIcon from "@mui/icons-material/Message";
import { site } from "data/site";

function StickyContactWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Show widget after scrolling down a bit
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setIsVisible(scrollTop > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Backdrop for mobile */}
      <Backdrop
        open={isOpen}
        onClick={handleClose}
        sx={{
          zIndex: 1200,
          display: { xs: "block", md: "none" },
          backgroundColor: "rgba(0, 0, 0, 0.5)",
        }}
      />

      {/* Contact Options Panel */}
      <Collapse in={isOpen} orientation="horizontal">
        <MKBox
          sx={{
            position: "fixed",
            bottom: { xs: 100, md: 110 },
            right: { xs: 20, md: 30 },
            zIndex: 1300,
            backgroundColor: "white",
            borderRadius: "20px",
            boxShadow: "0 12px 40px rgba(0,0,0,0.25)",
            p: 3,
            minWidth: { xs: "280px", md: "320px" },
            border: "2px solid rgba(0,0,0,0.1)",
            backdropFilter: "blur(10px)",
            background: "rgba(255, 255, 255, 0.95)",
          }}
        >
          <MKBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <MKTypography variant="h6" fontWeight="bold" color="dark" sx={{ opacity: 0.9 }}>
              Contact Us
            </MKTypography>
            <MKButton
              onClick={handleClose}
              size="small"
              sx={{
                minWidth: "auto",
                p: 0.5,
                color: "text.primary",
                backgroundColor: "rgba(0,0,0,0.05)",
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.1)",
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </MKButton>
          </MKBox>

          {/* Contact Methods */}
          <MKBox display="flex" flexDirection="column" gap={2}>
            {/* Phone */}
            <MKButton
              component="a"
              href={`tel:${site.phoneE164}`}
              variant="outlined"
              color="info"
              startIcon={<PhoneIcon />}
              sx={{
                justifyContent: "flex-start",
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "12px",
                py: 1.5,
                px: 2,
                borderWidth: "2px",
                borderColor: "info.main",
                color: "info.main",
                backgroundColor: "rgba(0,0,0,0.02)",
                "&:hover": {
                  backgroundColor: "info.main",
                  color: "white",
                  borderColor: "info.main",
                  transform: "translateY(-1px)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                },
              }}
            >
              Call {site.phone}
            </MKButton>

            {/* Email */}
            <MKButton
              component="a"
              href={`mailto:${site.email}`}
              variant="outlined"
              color="info"
              startIcon={<EmailIcon />}
              sx={{
                justifyContent: "flex-start",
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "12px",
                py: 1.5,
                px: 2,
                borderWidth: "2px",
                borderColor: "info.main",
                color: "info.main",
                backgroundColor: "rgba(0,0,0,0.02)",
                "&:hover": {
                  backgroundColor: "info.main",
                  color: "white",
                  borderColor: "info.main",
                  transform: "translateY(-1px)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                },
              }}
            >
              Send Email
            </MKButton>

            {/* Contact Form */}
            <MKButton
              component="a"
              href="/1/contact-us"
              variant="contained"
              color="info"
              startIcon={<MessageIcon />}
              sx={{
                justifyContent: "flex-start",
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "12px",
                py: 1.5,
                px: 2,
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 16px rgba(0,0,0,0.25)",
                },
              }}
            >
              Get Free Quote
            </MKButton>
          </MKBox>

          {/* Quick Info */}
          <MKBox mt={3} p={2} sx={{ 
            backgroundColor: "rgba(0,0,0,0.05)", 
            borderRadius: "12px",
            border: "1px solid rgba(0,0,0,0.1)",
          }}>
            <MKTypography variant="caption" color="text.primary" display="block" textAlign="center" fontWeight={500}>
              <strong>Business Hours:</strong><br />
              {site.hours.weekdays} | {site.hours.saturday} | {site.hours.sunday}
            </MKTypography>
          </MKBox>
        </MKBox>
      </Collapse>

      {/* Pulse Animation Ring - Behind the button */}
      {!isOpen && (
        <MKBox
          sx={{
            position: "fixed",
            bottom: { xs: 8, md: 18 },
            right: { xs: 8, md: 18 },
            zIndex: 1299, // Lower z-index than button
            width: { xs: 80, md: 88 },
            height: { xs: 80, md: 88 },
            borderRadius: "50%",
            border: "2px solid",
            borderColor: "info.main",
            opacity: 0.6,
            animation: "pulse 2s infinite",
            pointerEvents: "none",
            "@keyframes pulse": {
              "0%": {
                transform: "scale(1)",
                opacity: 0.6,
              },
              "100%": {
                transform: "scale(1.4)",
                opacity: 0,
              },
            },
          }}
        />
      )}

      {/* Floating Action Button */}
      <Fab
        onClick={handleToggle}
        color="info"
        sx={{
          position: "fixed",
          bottom: { xs: 20, md: 30 },
          right: { xs: 20, md: 30 },
          zIndex: 1300,
          width: { xs: 56, md: 64 },
          height: { xs: 56, md: 64 },
          boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
          "&:hover": {
            transform: "scale(1.1)",
            boxShadow: "0 6px 24px rgba(0,0,0,0.25)",
          },
          transition: "all 0.3s ease",
        }}
      >
        {isOpen ? <CloseIcon /> : <MessageIcon />}
      </Fab>
    </>
  );
}

export default StickyContactWidget;
